import { InventoryAnalytics } from "@/contexts/InventoryContext";
import { API } from "@/lib/api";
import { logRocketError } from "@/utils/logUtils";
import { InventoryError, InventoryErrorsResponse, InventoryItem, ListInventoryResponse, PartialInventoryResponse } from "@boxem/core-api-schema";

export async function getInventory(): Promise<InventoryItem[]> {
  let apiInventoryItems: InventoryItem[] = [];

  // first request, get the first 100 and the total count
  const params = new URLSearchParams();
  const pageSize = 1000;
  params.append("pageSize", `${pageSize}`);
  params.append("page",     `0`);

  const result = await API.get<ListInventoryResponse>(`/products`, params);
  apiInventoryItems.push(...result.data);

  if (result.data.length < result.total) {
    // if we have more than 'pageSize' total inventory items, make X requests in parallel to get the remainder,
    // where X is the number of remaining pages, given by:
    const totalPages = Math.ceil((result.total - result.data.length) / pageSize);
    const BATCH_SIZE = 15;
    const DELAY_MS = 2000;
    // Process pages in batches
    for (let batchStart = 1; batchStart <= totalPages; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, totalPages + 1);
      const batchRequests = Array.from({ length: batchEnd - batchStart }, (_, i) => {
        const params = new URLSearchParams();
        params.append("pageSize", `${pageSize}`);
        params.append("page", `${batchStart + i}`);
        return API.get<ListInventoryResponse>(`/products`, params);
      });

      const batchResults = await Promise.all(batchRequests);
      batchResults.forEach(result => apiInventoryItems.push(...result.data));

      if (batchEnd < totalPages) {
        await new Promise(resolve => setTimeout(resolve, DELAY_MS));
      }
    }
  }

  if (apiInventoryItems.length !== result.total) {
    logRocketError(`When loading inventory, we got a total of ${result.total} but only ended up loading ${apiInventoryItems.length} in total...`)
  }

  return apiInventoryItems;
}

export function getInventoryAnalytics() {
  return API.get<InventoryAnalytics>(
    `/inventories/analytics`
  );
}

export function updateListingBuyCost(listingId: number, buyCost: number) {
  return API.post<void>(`/products/${listingId}/buyCost`, {buyCost: buyCost});
}

export const getInventoryErrors = async (status: string): Promise<InventoryErrorsResponse> => {

  //Capture num of error instances
  const inventoryErrors: InventoryError[] = [];

  const fetchAllListings = async (offset: number) => {
    const params = new URLSearchParams();
    const limit = 10000;
    params.append("limit", `${limit}`);
    params.append("offset", `${offset}`);
    params.append("status", `${status}`);

    const batch = await API.get<PartialInventoryResponse>(`/inventories`, params);

    batch.map(listing => {
      const issues = typeof listing.issues === "string" ? JSON.parse(listing.issues) : listing.issues;
  
      issues.map(issue => {
        const existingListingErrorIndex = inventoryErrors.findIndex(listing => listing.error.code === issue.code);
  
        //Error doesn't exist in array
        if (existingListingErrorIndex === -1) {
          inventoryErrors.push({
            instances: 1,
            listings: [listing],
            error: issue,
            index: issue.code,
            fbmInstances: listing.fulfillmentType === "MFN" || listing.fulfillmentType === "FBM" ? 1 : 0,
            fbaInstances: listing.fulfillmentType === "FBA" ? 1 : 0,
          });
        } else {
          inventoryErrors[existingListingErrorIndex].instances++

          //Only add up to 500 viewable listings.
          if (inventoryErrors[existingListingErrorIndex].listings.length < 500) {
            inventoryErrors[existingListingErrorIndex].listings = [listing, ...inventoryErrors[existingListingErrorIndex].listings];
          }

          if (listing.fulfillmentType === "MFN" || listing.fulfillmentType === "FBM") {
            inventoryErrors[existingListingErrorIndex].fbmInstances++;
          } else if (listing.fulfillmentType === "FBA") {
            inventoryErrors[existingListingErrorIndex].fbaInstances++;
          }
        }
      });
    });

    if (batch.length !== limit) {
      return;
    }

    return fetchAllListings(offset + limit);
  };

  await fetchAllListings(0);

  return {
    data: inventoryErrors,
  }
};